import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './InternetList.css';
import iconClose from '../../assets/images/icon-back.svg';
import InternetDetail from './InternetDetail';
import { supabase } from '../../lib/supabase';
import { InternetProduct, TvProduct, TvProductPricing } from '../../types/products';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '../../contexts/AlertContext';

interface InternetListProps {
  onBack: () => void;
  onSwitchToPhone: () => void;
}

const InternetList: React.FC<InternetListProps> = ({ onBack, onSwitchToPhone }) => {
  const navigate = useNavigate();
  const { customer } = useAuth();
  const { showAlert } = useAlert();
  const [selectedCarrier, setSelectedCarrier] = useState('KT');
  const [selectedInternet, setSelectedInternet] = useState<InternetProduct | null>(null);
  const [hasRouter, setHasRouter] = useState(true);
  const [addTv, setAddTv] = useState(true);
  const [selectedTv, setSelectedTv] = useState<TvProduct | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isConsultExpanded, setIsConsultExpanded] = useState(false);

  // Supabase에서 불러온 상품 데이터
  const [internetProducts, setInternetProducts] = useState<InternetProduct[]>([]);
  const [tvProducts, setTvProducts] = useState<TvProduct[]>([]);
  const [tvPricingMap, setTvPricingMap] = useState<Map<string, TvProductPricing>>(new Map());
  const [loading, setLoading] = useState(true);

  // 통신사 이름을 DB 값으로 매핑
  const mapCarrierToDBValue = (carrier: string): string => {
    const carrierMap: { [key: string]: string } = {
      'SK': 'SKT',
      'KT': 'KT',
      'LG U+': 'LG',
      'SkyLife': 'SkyLife'
    };
    return carrierMap[carrier] || carrier;
  };

  // Supabase에서 상품 데이터 불러오기
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCarrier]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const dbCarrier = mapCarrierToDBValue(selectedCarrier);

      // 1. 인터넷 상품 조회
      const { data: internetData, error: internetError } = await supabase
        .from('internet_products')
        .select('*')
        .eq('carrier', dbCarrier)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (internetError) {
        console.error('Internet products error:', internetError);
        setInternetProducts([]);
        setTvProducts([]);
        return;
      }

      setInternetProducts(internetData || []);

      // 첫 번째 인터넷 상품 자동 선택
      if (internetData && internetData.length > 0) {
        setSelectedInternet(internetData[0]);
        // 선택된 인터넷에 맞는 TV 상품 로드
        await loadTvProductsForInternet(internetData[0].id);
      } else {
        setSelectedInternet(null);
        setTvProducts([]);
        setSelectedTv(null);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // 선택된 인터넷 상품에 맞는 TV 상품 로드
  const loadTvProductsForInternet = async (internetProductId: string) => {
    try {
      // 2. 해당 인터넷 상품과 매핑된 TV 상품 ID 조회
      const { data: mappings, error: mappingError } = await supabase
        .from('internet_product_tv_mappings')
        .select('tv_product_id, display_order')
        .eq('internet_product_id', internetProductId)
        .order('display_order', { ascending: true });

      if (mappingError) {
        console.error('TV mappings error:', mappingError);
        setTvProducts([]);
        setSelectedTv(null);
        return;
      }

      if (!mappings || mappings.length === 0) {
        setTvProducts([]);
        setSelectedTv(null);
        return;
      }

      const tvProductIds = mappings.map(m => m.tv_product_id);

      // 3. TV 상품 정보 조회
      const { data: tvData, error: tvError } = await supabase
        .from('tv_products')
        .select('*')
        .in('id', tvProductIds)
        .eq('is_active', true);

      if (tvError) {
        console.error('TV products error:', tvError);
        setTvProducts([]);
        setSelectedTv(null);
        return;
      }

      // 4. TV 가격 정보 조회
      const { data: pricingData, error: pricingError } = await supabase
        .from('tv_product_pricing')
        .select('*')
        .eq('internet_product_id', internetProductId)
        .in('tv_product_id', tvProductIds);

      if (pricingError) {
        console.error('TV pricing error:', pricingError);
      }

      // pricing을 Map으로 변환 (key: tv_product_id)
      const pricingMap = new Map<string, TvProductPricing>();
      pricingData?.forEach(pricing => {
        pricingMap.set(pricing.tv_product_id, pricing);
      });
      setTvPricingMap(pricingMap);

      // TV 상품을 mapping의 display_order 순서대로 정렬
      const sortedTvProducts = tvData?.sort((a, b) => {
        const orderA = mappings.find(m => m.tv_product_id === a.id)?.display_order || 0;
        const orderB = mappings.find(m => m.tv_product_id === b.id)?.display_order || 0;
        return orderA - orderB;
      }) || [];

      setTvProducts(sortedTvProducts);

      // 첫 번째 TV 자동 선택
      if (sortedTvProducts.length > 0) {
        setSelectedTv(sortedTvProducts[0]);
      } else {
        setSelectedTv(null);
      }
    } catch (error) {
      console.error('Error loading TV products:', error);
    }
  };

  // 인터넷 상품 선택 시 TV 상품 다시 로드
  const handleInternetSelect = async (internet: InternetProduct) => {
    setSelectedInternet(internet);
    await loadTvProductsForInternet(internet.id);
  };

  // TV 추가 비용 계산 (조합 가격 - 인터넷 단독 가격)
  const getTvPrice = (tvProduct: TvProduct | null): number => {
    if (!tvProduct || !selectedInternet) {
      return 0;
    }

    const pricing = tvPricingMap.get(tvProduct.id);
    if (!pricing) {
      return 0;
    }

    // 인터넷+TV 조합 가격
    const combinedPrice = hasRouter ? pricing.price_with_router : pricing.price_without_router;

    // 인터넷 단독 가격
    const internetPrice = hasRouter ? selectedInternet.price_with_router : selectedInternet.price_without_router;

    // TV 추가 비용 = 조합 가격 - 인터넷 가격
    const tvAdditionalCost = combinedPrice - internetPrice;

    return tvAdditionalCost || 0;
  };

  // 총 가격 계산
  const calculateTotalPrice = () => {
    let total = 0;

    // 인터넷 가격 (공유기 포함/미포함에 따라 다른 가격 적용)
    if (selectedInternet) {
      total += hasRouter ? selectedInternet.price_with_router : selectedInternet.price_without_router;
    }

    // TV 가격 (인터넷 속도 옵션에 따라 다른 가격)
    if (addTv && selectedTv) {
      total += getTvPrice(selectedTv);
    }

    return total;
  };

  const totalPrice = calculateTotalPrice();

  // 채팅 상담 요청
  const handleChatConsult = async () => {
    if (totalPrice === 0) {
      showAlert('상품을 선택해주세요.');
      return;
    }

    if (!customer) {
      showAlert('로그인이 필요합니다.');
      return;
    }

    console.log('Customer info:', customer);

    try {
      const dbCarrier = mapCarrierToDBValue(selectedCarrier);

      // 인터넷/TV 상품명 및 월요금 계산
      const internetName = selectedInternet
        ? `${selectedInternet.speed_value || ''}${selectedInternet.speed_unit || ''} ${selectedInternet.title || ''}`.trim()
        : null;
      const tvName = (addTv && selectedTv) ? selectedTv.title : null;
      const monthlyPrice = totalPrice || 0;

      // 현금 사은품 계산 (TV 추가 시 tv_product_pricing, 아닐 시 internet_products)
      const cashGift = (addTv && selectedTv)
        ? tvPricingMap.get(selectedTv.id)?.cash_gift || 0
        : selectedInternet?.cash_gift || 0;
      // 상품권
      const giftCard = (addTv && selectedTv)
        ? tvPricingMap.get(selectedTv.id)?.gift_card || ''
        : selectedInternet?.gift_card || '';

      // 채팅방 생성 (상품 정보 포함)
      const { data: chatRoom, error } = await supabase
        .from('chat_rooms')
        .insert([{
          customer_id: customer.id,
          status: 'waiting',
          carrier: dbCarrier,
          product_type: 'internet',
          internet_name: internetName,
          tv_name: tvName,
          monthly_price: monthlyPrice,
          cash_gift: cashGift > 0 ? cashGift : null
        }])
        .select()
        .single();

      if (error) throw error;

      // 초기 상담 요청 메시지 전송
      let productInfo = '안녕하세요! 다음 상품에 대해 상담 요청드립니다:\n\n';
      productInfo += '=== 인터넷/TV 상품 ===\n';
      if (selectedInternet) {
        const internetPrice = hasRouter ? selectedInternet.price_with_router : selectedInternet.price_without_router;
        productInfo += `▪ 인터넷: ${selectedInternet.speed_value || ''}${selectedInternet.speed_unit || ''} (${selectedInternet.title || ''}) - ${(internetPrice || 0).toLocaleString()}원/월\n`;
        productInfo += `  ${selectedInternet.description || ''}\n`;
        productInfo += `  공유기: ${hasRouter ? '포함' : '미포함'}\n`;
      }
      if (addTv && selectedTv) {
        const tvPrice = getTvPrice(selectedTv);
        productInfo += `▪ TV: ${selectedTv.title || ''} - ${(tvPrice || 0).toLocaleString()}원/월\n`;
        productInfo += `  ${selectedTv.description || ''}\n`;
      }
      productInfo += `\n💰 총 예상 금액: ${(totalPrice || 0).toLocaleString()}원/월`;
      if (cashGift > 0) {
        productInfo += `\n🎁 사은품: ${cashGift.toLocaleString()}원`;
      }
      if (giftCard) {
        productInfo += `\n🎫 상품권: ${giftCard}`;
      }

      const { error: messageError } = await supabase.from('messages').insert([{
        room_id: chatRoom.id,
        sender_id: customer.id,
        message_type: 'text',
        content: productInfo,
        is_read: false
      }]);

      if (messageError) {
        console.error('Error sending initial message:', messageError);
        // 메시지 전송 실패해도 채팅방으로 이동
      }

      // 상품 화면 닫기
      onBack();

      // 채팅 탭으로 전환하고 채팅방 열기
      navigate('/main', { state: { tab: 'chat', chatRoomId: chatRoom.id } });
    } catch (error: any) {
      console.error('Error creating chat room:', error);
      console.error('Error details:', error?.message, error?.details);
      showAlert('상담 요청 중 오류가 발생했습니다: ' + (error?.message || '알 수 없는 오류'));
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setDragStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const diff = dragStartY - currentY;

    // 위로만 드래그 가능하도록 제한
    if (diff > 0 && diff < 400) {
      setDragOffset(diff);
    } else if (diff < 0) {
      setDragOffset(0);
    }
  };

  const handleTouchEnd = () => {
    if (dragOffset > 100) {
      setIsBottomSheetExpanded(true);
      setDragOffset(0);
    } else {
      setIsBottomSheetExpanded(false);
      setDragOffset(0);
    }
  };

  if (showDetail) {
    return <InternetDetail onBack={() => setShowDetail(false)} />;
  }

  return (
    <div className="internet-list-container">
      <div className="internet-list-header">
        <div className="internet-list-header-wrapper">
          <img src={iconClose} alt="뒤로가기" className="header-icon" onClick={onBack} />
          <h1 className="internet-list-title">인터넷/TV</h1>
          <div className="header-icon" style={{ width: '24px', height: '24px' }}></div>
        </div>
      </div>

      <div className="internet-list-content">
        <h2 className="internet-list-page-title">인터넷/TV</h2>
        <div className="tab-selector">
          <button className="tab-btn active">인터넷 / TV</button>
          <button className="tab-btn" onClick={onSwitchToPhone}>핸드폰</button>
        </div>

        <div className="section">
          <div className="section-header">
            <h3 className="section-title">통신사</h3>
            <p className="section-desc">통신사를 바꾸면 해당 통신사의 요금만 표시돼요.</p>
          </div>
          <div className="chip-group">
            {['KT', 'LG U+', 'SK', 'SkyLife'].map((carrier) => (
              <button
                key={carrier}
                className={`chip ${selectedCarrier === carrier ? 'active' : ''}`}
                onClick={() => setSelectedCarrier(carrier)}
              >
                {carrier}
              </button>
            ))}
          </div>
        </div>

        <div className="divider" />

        <div className="section">
          <div className="section-header">
            <h3 className="section-title">인터넷</h3>
            <p className="section-desc">인터넷은 필수 선택입니다.</p>
          </div>
          {loading ? (
            <div className="loading">상품을 불러오는 중...</div>
          ) : (
            <div className="card-grid">
              {internetProducts.map((product) => {
                const displayPrice = hasRouter ? product.price_with_router : product.price_without_router;
                return (
                  <div
                    key={product.id}
                    className={`plan-card ${selectedInternet?.id === product.id ? 'active' : ''}`}
                    onClick={() => handleInternetSelect(product)}
                  >
                    <p className="plan-speed">{`${product.speed_value || ''}${product.speed_unit || ''}`}</p>
                    <p className="plan-desc">{`${product.title || ''} · ${product.description || ''}`}</p>
                    <div className="plan-price">
                      <span className="price">{`${(displayPrice || 0).toLocaleString()} 원`}</span>
                      <span className="period">/월</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="checkbox-group">
            <button
              className={`checkbox-card ${hasRouter ? 'active' : ''}`}
              onClick={() => setHasRouter(!hasRouter)}
            >
              <div className="checkbox">{hasRouter && '✓'}</div>
              <span>공유기와 함께</span>
            </button>
            <button
              className={`checkbox-card ${addTv ? 'active' : ''}`}
              onClick={() => setAddTv(!addTv)}
            >
              <div className="checkbox">{addTv && '✓'}</div>
              <span>TV와 함께</span>
            </button>
          </div>

          {addTv && (
            <>
              <div className="divider" />
              <div className="section-header">
                <h3 className="section-title">TV</h3>
                <p className="section-desc">TV 상품을 선택해주세요.</p>
              </div>
              {loading ? (
                <div className="loading">상품을 불러오는 중...</div>
              ) : (
                <div className="card-grid">
                  {tvProducts.map((product) => {
                    const tvPrice = getTvPrice(product);
                    return (
                      <div
                        key={product.id}
                        className={`plan-card ${selectedTv?.id === product.id ? 'active' : ''}`}
                        onClick={() => setSelectedTv(product)}
                      >
                        <p className="plan-speed">{product.title || 'TV'}</p>
                        <p className="plan-desc">{product.description || ''}</p>
                        <div className="plan-price">
                          <span className="price">{`${(tvPrice || 0).toLocaleString()} 원`}</span>
                          <span className="period">/월</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

      </div>

      {isConsultExpanded && (
        <div
          className="overlay"
          onClick={() => setIsConsultExpanded(false)}
        />
      )}

      <div
        className={`bottom-sheet ${isBottomSheetExpanded ? 'expanded' : ''}`}
        style={{ transform: `translateY(-${dragOffset}px)` }}
      >
        <div
          className="bottom-sheet-handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="handle-bar"></div>
        </div>
        <button
          className="detail-expand-btn"
          onClick={() => setIsConsultExpanded(!isConsultExpanded)}
        >
          {isConsultExpanded ? '상세내역 닫기 ▼' : '상세내역 확인 ▲'}
        </button>
        <div className="bottom-sheet-content">
          <div className="price-row">
            <div className="price-label">
              <span className="label-text">기본 요금</span>
              <span className="label-sub">(매월)</span>
            </div>
            <span className="price-value">{totalPrice.toLocaleString()} 원</span>
          </div>

          <div className="benefit-section">
            {isConsultExpanded && (
              <div className="price-detail-expanded">
                {/* 가로 구분선 */}
                <div className="horizontal-divider-list" />

                {/* 인터넷/TV 섹션 */}
                <div className="detail-section">
                  <div className="detail-row main">
                    <span className="detail-label">인터넷/TV</span>
                    <span className="detail-value">{totalPrice.toLocaleString()} 원</span>
                  </div>
                  {selectedInternet && (
                    <>
                      <div className="detail-row sub">
                        <span className="detail-label">{`인터넷 ${selectedInternet.speed_value || ''}${selectedInternet.speed_unit || ''} (${selectedInternet.title || ''})`}</span>
                        <span className="detail-value">
                          {((hasRouter ? selectedInternet.price_with_router : selectedInternet.price_without_router) || 0).toLocaleString()} 원
                        </span>
                      </div>
                      {hasRouter && (
                        <div className="detail-row sub">
                          <span className="detail-label">공유기 포함</span>
                          <span className="detail-value">포함됨</span>
                        </div>
                      )}
                    </>
                  )}
                  {addTv && selectedTv && (
                    <div className="detail-row sub">
                      <span className="detail-label">{`TV ${selectedTv.title || ''}`}</span>
                      <span className="detail-value">{(getTvPrice(selectedTv) || 0).toLocaleString()} 원</span>
                    </div>
                  )}
                </div>

                {/* 사은품/상품권 섹션 */}
                {(() => {
                  // TV 추가 시 tv_product_pricing의 값, 아닐 시 internet_products의 값
                  const cashGift = (addTv && selectedTv)
                    ? tvPricingMap.get(selectedTv.id)?.cash_gift
                    : selectedInternet?.cash_gift;
                  const giftCard = (addTv && selectedTv)
                    ? tvPricingMap.get(selectedTv.id)?.gift_card
                    : selectedInternet?.gift_card;

                  const hasCashGift = cashGift && cashGift > 0;
                  const hasGiftCard = !!giftCard;

                  return (hasCashGift || hasGiftCard) ? (
                    <>
                      <div className="horizontal-divider-list" />
                      <div className="detail-section">
                        {hasCashGift && (
                          <div className="detail-row main">
                            <span className="detail-label">사은품</span>
                            <span className="detail-value">{cashGift.toLocaleString()}원</span>
                          </div>
                        )}
                        {hasGiftCard && (
                          <div className="detail-row main">
                            <span className="detail-label">상품권</span>
                            <span className="detail-value">{giftCard}</span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : null;
                })()}

                {/* 가로 구분선 */}
                <div className="horizontal-divider-list" />

                {/* 월 납부 예상 금액 */}
                <div className="detail-row total">
                  <div className="total-label-group">
                    <span className="total-label">월 납부 예상 금액</span>
                    <span className="total-sub">인터넷 + TV</span>
                  </div>
                  <span className="total-value">{totalPrice.toLocaleString()} 원</span>
                </div>

                {/* 상담 요청 버튼 */}
                <button className="consult-request-btn" onClick={handleChatConsult}>
                  상담 요청
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternetList;
